import { createElement } from 'react';

import { cn } from '../../utils/cn';
import type { TextProps } from './Text.types';

/**
 * Web Text — renders a `<span>` (or chosen `as` element) with inherited
 * font/color so that the same className produces the same visual output as
 * react-native's Text on mobile.
 *
 * `numberOfLines` is approximated with CSS line-clamp when > 0. `selectable`
 * maps to `user-select` via Tailwind classes the caller can override.
 */
const RESET_CLASSES = 'font-inherit text-inherit text-current m-0 p-0';

export function Text({
  as = 'span',
  className,
  children,
  testID,
  accessibilityLabel,
  numberOfLines,
  selectable,
  webProps,
  nativeProps: _nativeProps,
}: TextProps) {
  // The clamp is split on purpose: three STATIC classes plus one inline value.
  //
  // `[-webkit-line-clamp:${numberOfLines}]` used to be part of this string, and
  // it never worked on web: Tailwind extracts candidates from source TEXT, so
  // an interpolated arbitrary property is emitted for no value at all. Every
  // clamped Text therefore rendered as a legacy `-webkit-box` with
  // `overflow: hidden` and NO clamp — it wrapped and got clipped instead of
  // ellipsizing, which only showed up once a row got narrow enough to truncate.
  //
  // The count is a runtime number, so inline style is the only mechanism that
  // can carry it (core `line-clamp-N` classes would cap us at a lookup table,
  // and `numberOfLines` is not always a literal — see `CardTagLabel`). The
  // caller's own `webProps.style` still wins, spread after.
  const clamped = !!numberOfLines && numberOfLines > 0;
  const lineClamp = clamped
    ? 'overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical]'
    : '';
  const clampStyle = clamped
    ? { WebkitLineClamp: numberOfLines, ...webProps?.style }
    : webProps?.style;
  const selectability =
    selectable === false ? 'select-none' : selectable === true ? 'select-text' : '';

  return createElement(
    as,
    {
      ...webProps,
      className: cn(RESET_CLASSES, lineClamp, selectability, className),
      style: clampStyle,
      'aria-label': accessibilityLabel ?? webProps?.['aria-label'],
      'data-testid': testID,
    },
    children,
  );
}
